import { useAuth } from '../auth/useAuth'
import { Menu } from 'lucide-preact'
import { useLocation } from 'preact-iso'
import { useMemo } from 'preact/hooks'
import { LAYOUT_CONSTANTS } from '../constants/layout'
import { CompanySelector } from './common/CompanySelector'
import { WorkspaceSelector } from './common/WorkspaceSelector'
import { UserMenu } from './common/UserMenu'
import type { ClientWorkspaceSummaryViewDto } from '@/types/company'
import { useCompany } from '@/hooks/useCompany'
import { useCompanyWorkspaceNav } from '@/hooks/useCompanyWorkspaceNav'
import { routes } from '@/utils/routes'

interface TopNavigationProps {
  onMenuToggle?: () => void
  onSettingsClick?: () => void
  onWorkspaceChange?: (workspace: ClientWorkspaceSummaryViewDto) => void
  hideCompanyWorkspaceSelector?: boolean
}

export function TopNavigation({ onMenuToggle, onSettingsClick, onWorkspaceChange, hideCompanyWorkspaceSelector }: TopNavigationProps) {
  const { user, logout, isAuthenticated, canManageCustomers, userProfile, companySummary } = useAuth()
  const location = useLocation()
  const { company, workspaces } = useCompany()

  const {
    routeCompanyId,
    companies,
    showCompanySelector,
    currentCompanyWorkspaces,
    currentWorkspaceId,
    handleCompanyChange,
    handleWorkspaceChange,
  } = useCompanyWorkspaceNav({ onWorkspaceChange })

  // Get company name from workspace summaries since company object may be null
  const companyName = useMemo(() => company?.Name || workspaces?.[0]?.ClientCompanyName || null, [company, workspaces])

  const handleSettingsClick = () => {
    onSettingsClick?.()
  }

  // Determine if we should show "Back to Home Company" in the user menu
  const homeCompanyId = userProfile?.ClientCompanyId
  const showBackToHome = Boolean(
    isAuthenticated && canManageCustomers && homeCompanyId && routeCompanyId && homeCompanyId !== routeCompanyId
  )
  const backToHomeLabel = companySummary?.Name ? `Back to ${companySummary.Name}` : undefined
  const handleBackToHome = () => {
    if (homeCompanyId) {
      location.route(routes.company(homeCompanyId))
    }
  }

  return (
    <>
      {/* Full-width header background */}
      <div
        className="w-full bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700"
        style={{ height: `${LAYOUT_CONSTANTS.TOP_NAV_HEIGHT}px` }}
      >
        <header
          className="lg:ml-64 h-full"
        >
          <div className="px-4 sm:px-6 lg:px-8 h-full">
            <div className="flex justify-between items-center h-full">
              {/* Left side: Mobile menu button */}
              <div className="flex items-center gap-2">
                {/* Mobile menu button */}
                <button
                  onClick={onMenuToggle}
                  className="lg:hidden p-2 rounded-md text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </div>

              {/* Right side: Company Selector, Workspace Selector, Separator, and User menu */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Company & Workspace selectors — hidden on mobile, visible sm+ */}
                {isAuthenticated && !hideCompanyWorkspaceSelector && (
                  <div className="hidden sm:flex items-center gap-3">
                    {showCompanySelector && routeCompanyId ? (
                      <CompanySelector
                        selectedCompanyId={routeCompanyId}
                        onCompanyChange={handleCompanyChange}
                        companies={companies}
                      />
                    ) : (
                      companyName && (
                        <div className="flex items-center max-w-xs truncate text-slate-700 dark:text-slate-200 text-sm font-medium">
                          <span className="truncate">{companyName}</span>
                        </div>
                      )
                    )}

                    {/* Visual separator between company and workspace */}
                    {(showCompanySelector || companyName) && (
                      <div className="text-slate-400 dark:text-slate-500 text-sm">/</div>
                    )}

                    {/* Workspace Selector */}
                    {onWorkspaceChange && (
                      <WorkspaceSelector
                        selectedWorkspaceId={currentWorkspaceId || undefined}
                        onWorkspaceChange={handleWorkspaceChange}
                        workspaces={currentCompanyWorkspaces || null}
                      />
                    )}

                    {/* Separator before user menu */}
                    <div className="h-6 w-px bg-slate-300 dark:bg-slate-600"></div>
                  </div>
                )}

                {/* User menu — always visible */}
                {isAuthenticated && user && (
                  <UserMenu
                    user={user}
                    profilePicUrl={userProfile ? userProfile.AvatarUrl : undefined}
                    onLogout={logout}
                    onSettingsClick={handleSettingsClick}
                    onBackToHome={showBackToHome ? handleBackToHome : undefined}
                    backToHomeLabel={showBackToHome ? backToHomeLabel : undefined}
                  />
                )}
              </div>
            </div>
          </div>
        </header>
      </div>
    </>
  )
}
